import {
  AppShellTrigger,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  Button,
  Icon,
  Text,
  useIsMobile,
  useShellTier,
} from "@salestrak/ui";
import * as React from "react";

import { SettingsMobileHeaderContext } from "#app/components/navigation/settings-mobile-header";
import { noop } from "#app/lib/utils/noop";
import type { Omittable } from "@salestrak/ui";

const SLOT_NAMES = {
  breadcrumb: "PageHeaderBreadcrumb",
  back: "PageHeaderBack",
  title: "PageHeaderTitle",
  subtitle: "PageHeaderSubtitle",
  actions: "PageHeaderActions",
} as const;

type BackDescriptor = { label: string; onClick: () => void };

function isBackDescriptor(value: unknown): value is BackDescriptor {
  return (
    typeof value === "object" &&
    value !== null &&
    !React.isValidElement(value) &&
    "label" in value &&
    "onClick" in value
  );
}

function findSlot(
  children: React.ReactNode,
  name: string,
): React.ReactElement | null {
  let found: React.ReactElement | null = null;
  React.Children.forEach(children, (child) => {
    if (found || !React.isValidElement(child)) return;
    if ((child.type as { displayName?: string })?.displayName === name) {
      found = child;
    }
  });
  return found;
}

/**
 * The ONE width of the centered settings genre. Every centered settings
 * surface — this header's wrap, the group columns, the detail views — reads
 * it, so the header and the content below it can never mis-align.
 */
export const SETTINGS_CONTENT_MAX_WIDTH = 620;

/**
 * Trailing over-scroll gutter for the centered settings genre: mounted as the
 * LAST child of a page's content column so the final card can scroll well
 * past the fold and trailing options read at a comfortable height instead of
 * pinning to the viewport bottom.
 */
export function SettingsPageGutter(): React.ReactElement {
  return <Box aria-hidden height={96} shrink={false} />;
}

function centeredWrap(content: React.ReactNode) {
  return (
    // Outer `md`, matching the content wrapper's own paddingX exactly: with
    // any other value the header's 620 column centers inside a different
    // content box than the page column, and the two rails drift apart as
    // soon as the pane is narrower than the column.
    <Box direction="column" align="center" paddingX="md">
      {/* The alignment rail of the settings genre is the TEXT, not the card
        * edge: rows pad 16px inside their card, so all out-of-card text (page
        * title, subtitle, back link, section labels) carries the same 16px
        * inset and the cards bleed past the text on both sides. */}
      <Box fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH} paddingX="md">
        {content}
      </Box>
    </Box>
  );
}

type PageHeaderProps = Omittable<{
  children?: React.ReactNode;
  /** On desktop, center the header content within a max-width container to align with page content. */
  centered?: boolean;
  /** String wraps in single-segment breadcrumb chrome; JSX renders as-is (pass `<PageHeaderTitle>` for big titles, `<Breadcrumb>` for multi-segment trails). */
  title?: string | React.ReactElement;
  /** String wraps in `<PageHeaderSubtitle>`; JSX renders as-is. */
  subtitle?: string | React.ReactElement;
  /** `{label,onClick}` wraps in `<PageHeaderBack>`; JSX renders as-is. */
  back?: BackDescriptor | React.ReactElement;
  /** Wraps in `<PageHeaderActions>` unless already a `<PageHeaderActions>` element. */
  actions?: React.ReactNode;
  /** The page pins its own `SettingsBackBar` above this header. The header
   *  then behaves as if the back were in flow — the title stacks on its own
   *  row — while skipping the phantom leading-back row a centered no-back
   *  page would otherwise reserve (the sticky bar already owns that space). */
  pinnedBack?: boolean;
}>;

export function PageHeader({
  children,
  centered = false,
  title,
  subtitle,
  back,
  actions,
  pinnedBack = false,
}: PageHeaderProps) {
  const isMobile = useIsMobile();
  const hasSettingsMobileHeader = React.useContext(SettingsMobileHeaderContext);

  const synthesized: React.ReactNode[] = [];

  if (title != null) {
    synthesized.push(
      typeof title === "string" ? (
        <PageHeaderBreadcrumb>
          <BreadcrumbItem>
            {/* Pass the raw string — BreadcrumbPage carries the page-title
                type (base + semibold) and truncates. Nesting another
                (default-size) Text inside it clips ascenders: the outer
                truncate's overflow-hidden cuts the taller inner line box. */}
            <BreadcrumbPage>{title}</BreadcrumbPage>
          </BreadcrumbItem>
        </PageHeaderBreadcrumb>
      ) : (
        title
      ),
    );
  }

  if (subtitle != null) {
    synthesized.push(
      typeof subtitle === "string" ? (
        <PageHeaderSubtitle>{subtitle}</PageHeaderSubtitle>
      ) : (
        subtitle
      ),
    );
  }

  if (back != null) {
    synthesized.push(
      isBackDescriptor(back) ? (
        <PageHeaderBack label={back.label} onClick={back.onClick} />
      ) : (
        back
      ),
    );
  }

  if (actions != null) {
    const isAlreadyWrapped =
      React.isValidElement(actions) &&
      (actions.type as { displayName?: string })?.displayName ===
        SLOT_NAMES.actions;
    synthesized.push(
      isAlreadyWrapped ? actions : <PageHeaderActions>{actions}</PageHeaderActions>,
    );
  }

  const allChildren: React.ReactNode =
    synthesized.length > 0
      ? [...synthesized, ...React.Children.toArray(children)]
      : children;

  const breadcrumbEl = findSlot(allChildren, SLOT_NAMES.breadcrumb);
  const backEl = findSlot(allChildren, SLOT_NAMES.back);
  const titleEl = findSlot(allChildren, SLOT_NAMES.title);
  const subtitleEl = findSlot(allChildren, SLOT_NAMES.subtitle);
  const actionsEl = findSlot(allChildren, SLOT_NAMES.actions);

  const hasBack = !!backEl;
  // Centered headers center at EVERY width: the content column below centers
  // regardless of breakpoint, so a mobile-only flush-left title drifted off
  // the column's rail the moment the pane narrowed.
  const shouldCenter = centered;
  const stackTitle =
    hasBack ||
    shouldCenter ||
    pinnedBack ||
    (isMobile && hasBack && hasSettingsMobileHeader);

  // The shell-wide FIRST-LINE contract: a screen's opening row is 8px padding
  // + a 28px content line (44px block, centerline at 22px) so it sits on the
  // same optical line as the sidebar pane's title (SidebarPaneHeader pins the
  // same 44px block). Applies to the breadcrumb/actions genre; the
  // centered/stacked settings genre is a document, not a first line, and
  // keeps its roomier inset. First-line actions are the 24px rung (Button
  // xs/icon-xs); anything over 28px stretches the row off the contract.
  const firstLine = !stackTitle && !shouldCenter;

  const navLeft =
    breadcrumbEl ??
    backEl ??
    // The phantom back row reserves the genre's leading-back line so titles
    // sit on one optical line across settings pages. A `pinnedBack` page owns
    // that top space with its own sticky SettingsBackBar, so it opts out.
    (shouldCenter && !isMobile && !pinnedBack ? <InvisibleBackSpacer /> : null);

  const showNavRow = !!navLeft || !stackTitle;

  const navRow = showNavRow ? (
    <Box
      direction="row"
      align="center"
      justify={!stackTitle && actionsEl ? "between" : "start"}
      className={shouldCenter && stackTitle ? "ml-6" : undefined}
      minHeight={firstLine ? 28 : undefined}
    >
      <Box direction="row" align="center" gap="sm" shrink>
        {navLeft}
        {!stackTitle && titleEl}
      </Box>
      {!stackTitle && actionsEl}
    </Box>
  ) : null;

  const titleRow =
    stackTitle && titleEl ? (
      <Box
        direction="row"
        align="center"
        justify={actionsEl ? "between" : "start"}
        gap="sm"
      >
        {titleEl}
        {actionsEl}
      </Box>
    ) : null;

  const subtitleSlot = subtitleEl
    ? shouldCenter
      ? centeredWrap(subtitleEl)
      : subtitleEl
    : null;

  return (
    <Box
      direction="column"
      // First-line genre: the 8px rung of the shared contract above. The
      // settings document genre keeps the `md` inset so its big title sits
      // equidistant from the card edges — and on mobile it gains extra TOP
      // room (`lg`) so the large title breathes below the mobile header's
      // border instead of crowding it.
      paddingTop={
        shouldCenter && isMobile ? "lg" : isMobile || firstLine ? "sm" : "md"
      }
      paddingBottom={isMobile || firstLine ? "sm" : "md"}
      // One 16px inline rail at every viewport: the mobile settings header
      // above insets `md`, so a title on a tighter `sm` rail sat visibly
      // left of the header's own icon.
      paddingX={shouldCenter ? undefined : "md"}
      gap={stackTitle ? "xs" : undefined}
    >
      {navRow ? (shouldCenter && !stackTitle ? centeredWrap(navRow) : navRow) : null}
      {titleRow ? (shouldCenter ? centeredWrap(titleRow) : titleRow) : null}
      {subtitleSlot}
    </Box>
  );
}
PageHeader.displayName = "PageHeader";

function InvisibleBackSpacer() {
  return (
    <Box pointerEvents="none" opacity={0}>
      <PageHeaderBack label="Back" onClick={noop} />
    </Box>
  );
}

type PageHeaderBreadcrumbProps = {
  /** Breadcrumb segments — must contain `BreadcrumbItem`/`BreadcrumbSeparator` children. For a single label, use `<PageHeader title="...">` instead. */
  children: React.ReactNode;
};

export function PageHeaderBreadcrumb({ children }: PageHeaderBreadcrumbProps) {
  // On compact the navigation is a drawer with no rail on screen, so the
  // page breadcrumb carries the trigger that summons it. Medium needs no
  // header trigger: the pinned rail's `>>` chip already owns expanding the
  // pane. Settings screens render theirs in `SettingsMobileHeader` instead —
  // suppress the duplicate.
  const tier = useShellTier();
  const hasSettingsMobileHeader = React.useContext(SettingsMobileHeaderContext);
  const showMenu = tier === "compact" && !hasSettingsMobileHeader;
  return (
    <Box direction="row" align="center" gap="sm" shrink>
      {showMenu && <AppShellTrigger />}
      <Breadcrumb>
        <BreadcrumbList>{children}</BreadcrumbList>
      </Breadcrumb>
    </Box>
  );
}
PageHeaderBreadcrumb.displayName = SLOT_NAMES.breadcrumb;

type PageHeaderBackProps = {
  /** String or JSX, like every PageHeader prop: a string wraps in the muted
   *  back Text; a node renders as-is (an entity chip naming the target). */
  label: React.ReactNode;
  onClick: () => void;
};

/**
 * Pinned back action for scrolling settings detail pages (the reference
 * settings-detail pattern). Mount it as a SIBLING ABOVE the page's ScrollArea
 * inside a `position="relative"` page root; drop the in-flow `PageHeaderBack` from
 * the scrolled header and pass it `pinnedBack`; and put `SettingsBackBarClearance`
 * as the ScrollArea's first child.
 *
 * It is an OPAQUE band OVER (outside) the scroll viewport — an absolute
 * overlay, not inside the ScrollArea — so scrolled content passes cleanly
 * UNDER it and is hidden, with a short gradient fade tail below the solid band
 * for a soft dissolve. The band paints the PAGE's own surface fill (verified:
 * the content pane is `--background` on desktop, `--sunken` on mobile where
 * the pane sits flush), fill ONLY (never `surface=`, which also casts the
 * pane drop shadow — that shadow strip was the "different color" bug). The
 * scroll-fade MASK route was abandoned: Tailwind's 6-layer intersect mask did
 * not render in Chromium, and a mask over the viewport would fade the pinned
 * back itself.
 *
 * The button sits a few px off the pane's LEFT edge (reference placement), not
 * on the centered content column's rail. The band takes the `md` inset the
 * settings mobile header uses and the back keeps its own padding
 * (`flush={false}`), so the chevron lands on the SAME x as that header's
 * panel/toggle icon directly above it (~24px in).
 */
const BACK_BAR_TAIL = 12; // px — the fade tail below the solid band

export function SettingsBackBar({ label, onClick }: PageHeaderBackProps) {
  // The page fill the band sits on: ContentPane paints `background` at
  // EVERY viewport (mobile only drops the card chrome, never the fill).
  const fillClass = "bg-background";
  const fadeClass = "bg-linear-to-b from-background to-transparent";
  return (
    <Box
      direction="column"
      insetX={0}
      pointerEvents="none"
      position="absolute"
      top={0}
      zIndex={10}
    >
      <Box
        align="center"
        className={fillClass}
        direction="row"
        minHeight={28}
        paddingX="md"
        paddingY="sm"
      >
        <Box pointerEvents="auto" width="fit-content">
          <PageHeaderBack flush={false} label={label} onClick={onClick} />
        </Box>
      </Box>
      <Box aria-hidden className={fadeClass} height={BACK_BAR_TAIL} />
    </Box>
  );
}

/** At-rest clearance paired with {@link SettingsBackBar}, the ScrollArea's
 *  first child. Reserves the solid band (paddingY sm + 28px row = 44) plus the
 *  fade tail, so the title starts fully clear of both at rest. */
export function SettingsBackBarClearance() {
  return <Box aria-hidden height={44 + BACK_BAR_TAIL} shrink={false} />;
}

export function PageHeaderBack({
  label,
  onClick,
  flush = true,
}: PageHeaderBackProps & Omittable<{ flush?: boolean }>) {
  return (
    // `flush` (default): `-ml-2.5` cancels the sm size's own horizontal
    // padding so the chevron sits on the container edge — the in-flow header
    // where the back is the leading element. The pinned SettingsBackBar
    // passes `flush={false}` so the button keeps its padding and the chevron
    // lands a touch inside the content rail rather than hard against it.
    <Button
      variant="ghost"
      size="sm"
      className={flush ? "-ml-2.5" : undefined}
      onClick={onClick}
    >
      <Icon name="chevron-left" color="muted" />
      {typeof label === "string" ? (
        <Text variant="muted">{label}</Text>
      ) : (
        label
      )}
    </Button>
  );
}
PageHeaderBack.displayName = SLOT_NAMES.back;

type PageHeaderTitleProps = Omittable<{
  children: React.ReactNode;
  /** Inline content rendered next to the title (e.g. icon, badge). */
  adornment?: React.ReactNode;
  /** Controls the title text size. Default renders at 2xl, "sm" renders at lg for breadcrumb-style headers. */
  size?: "default" | "sm";
}>;

export function PageHeaderTitle({
  children,
  adornment,
  size = "default",
}: PageHeaderTitleProps) {
  const isMobile = useIsMobile();
  // Titles stay large on narrow viewports (reference parity) — the old
  // blanket `isMobile ? "sm"` collapsed every page title to 14px. Default
  // titles step 2xl→xl on mobile; the breadcrumb-style `sm` variant steps
  // lg→base.
  const responsiveSize =
    size === "sm" ? (isMobile ? "base" : "lg") : isMobile ? "xl" : "2xl";
  return (
    <Box direction="row" align="center" gap="sm" shrink>
      {/* The page's principal heading — h1 so it carries the heading role
          screen readers and tests navigate by, not a bare span. */}
      <Text as="h1" size={responsiveSize} weight="semibold" truncate>
        {children}
      </Text>
      {adornment}
    </Box>
  );
}
PageHeaderTitle.displayName = SLOT_NAMES.title;

export function PageHeaderSubtitle({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Text size="sm" variant="muted">
      {children}
    </Text>
  );
}
PageHeaderSubtitle.displayName = SLOT_NAMES.subtitle;

export function PageHeaderActions({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    // `flex="none"`: beside a long title the actions keep their width and the
    // title's own `truncate` takes the squeeze, so a menu never gets crushed
    // against (or under) the text.
    <Box direction="row" align="center" gap="xs" flex="none">
      {children}
    </Box>
  );
}
PageHeaderActions.displayName = SLOT_NAMES.actions;
